import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth } from '../../app/useAuth'
import { Icon } from '../../components/icons'
import { Button, EmptyState, Field, Input, PageHeader, Select, Spinner } from '../../components/ui'
import { toast } from '../../components/toast'
import {
  api,
  saveBlob,
  type AuditEvent,
  type AuditFilters,
} from '../../lib/api/client'
import './audit.css'

const ACTIONS = [
  ['auth.sign_in', 'Signed in'],
  ['auth.sign_out', 'Signed out'],
  ['paper.create', 'Created a paper'],
  ['paper.update', 'Updated a paper'],
  ['paper.workflow', 'Changed paper workflow'],
  ['paper.archive', 'Archived a paper'],
  ['paper.restore', 'Restored a paper'],
  ['paper.print', 'Updated printing'],
  ['picture.upload', 'Uploaded a picture'],
  ['ai.rephrase_requested', 'Requested an AI rephrasing'],
  ['ai.rephrase_accepted', 'Accepted an AI rephrasing'],
  ['ai.picture_generated', 'Generated an AI picture'],
  ['ai.picture_saved', 'Saved an AI picture'],
  ['people.invite', 'Invited a colleague'],
  ['people.update', 'Updated a colleague'],
  ['people.default_reviewer', 'Changed the default reviewer'],
  ['school.update', 'Updated school details'],
  ['school.retention', 'Changed archive retention'],
  ['template.update', 'Changed a paper header'],
  ['ai.settings', 'Changed AI settings'],
  ['platform.school', 'Changed a school'],
  ['platform.settings', 'Changed platform settings'],
  ['platform.branding', 'Changed platform branding'],
  ['billing.checkout', 'Started subscription checkout'],
  ['billing.portal', 'Opened subscription management'],
  ['billing.cancel', 'Cancelled a subscription'],
  ['audit.export', 'Exported the audit log'],
] as const

type FilterState = {
  q: string
  schoolId: string
  action: string
  from: string
  to: string
}

const EMPTY_FILTERS: FilterState = { q: '', schoolId: '', action: '', from: '', to: '' }

export function AuditLog({
  schoolId,
  embedded = false,
}: {
  schoolId?: string
  embedded?: boolean
}) {
  const { has } = useAuth()
  const isSiteAdmin = has('admin')
  const [filters, setFilters] = useState<FilterState>({ ...EMPTY_FILTERS, schoolId: schoolId ?? '' })
  const [page, setPage] = useState(1)
  const queryFilters = useMemo(
    () => toApiFilters(filters, page, schoolId),
    [filters, page, schoolId],
  )
  const events = useQuery({
    queryKey: ['audit-events', queryFilters],
    queryFn: () => api.auditEvents(queryFilters),
  })
  const schools = useQuery({
    queryKey: ['platform', 'schools'],
    queryFn: api.platformSchools,
    enabled: isSiteAdmin && !schoolId,
  })
  const exportLog = useMutation({
    mutationFn: () => api.exportAuditEvents({ ...queryFilters, page: undefined, per_page: undefined }),
    onSuccess: (blob) => {
      saveBlob(blob, `teachcraftai-audit-${new Date().toISOString().slice(0, 10)}.csv`)
      toast('Audit log exported.')
    },
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const update = (key: keyof FilterState, value: string) => {
    setFilters((current) => ({ ...current, [key]: value }))
    setPage(1)
  }
  const reset = () => {
    setFilters({ ...EMPTY_FILTERS, schoolId: schoolId ?? '' })
    setPage(1)
  }

  const content = (
    <>
      <section className="tc-audit-filters" aria-label="Audit filters">
        <Field label="Search">
          <Input
            value={filters.q}
            onChange={(event) => update('q', event.target.value)}
            placeholder="Person, email, school, or activity"
          />
        </Field>
        {isSiteAdmin && !schoolId && (
          <Field label="School">
            <Select value={filters.schoolId} onChange={(event) => update('schoolId', event.target.value)}>
              <option value="">All schools</option>
              {(schools.data ?? []).map((school) => (
                <option key={school.id} value={school.id}>
                  {school.name}{school.branch ? ` — ${school.branch}` : ''}
                </option>
              ))}
            </Select>
          </Field>
        )}
        <Field label="Activity">
          <Select value={filters.action} onChange={(event) => update('action', event.target.value)}>
            <option value="">All activity</option>
            {ACTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </Select>
        </Field>
        <Field label="From">
          <Input type="date" value={filters.from} onChange={(event) => update('from', event.target.value)} />
        </Field>
        <Field label="To">
          <Input type="date" value={filters.to} onChange={(event) => update('to', event.target.value)} />
        </Field>
        <div className="tc-audit-filter-actions">
          <Button variant="ghost" onClick={reset}>Reset filters</Button>
        </div>
      </section>

      {events.isLoading ? (
        <Spinner label="Loading audit activity" />
      ) : events.isError ? (
        <EmptyState
          icon={<Icon.audit size={22} />}
          title="Audit activity could not be loaded"
          message={events.error instanceof Error ? events.error.message : 'Try again in a moment.'}
          action={<Button variant="secondary" onClick={() => events.refetch()}>Try again</Button>}
        />
      ) : !events.data?.events.length ? (
        <EmptyState
          icon={<Icon.audit size={22} />}
          title="No matching activity"
          message="Change or clear the filters to see more audit events."
        />
      ) : (
        <AuditTable events={events.data.events} showSchool={isSiteAdmin && !schoolId} />
      )}

      {events.data && events.data.page_count > 1 && (
        <nav className="tc-audit-pagination" aria-label="Audit pages">
          <Button
            variant="secondary"
            disabled={page <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </Button>
          <span>Page {events.data.page} of {events.data.page_count}</span>
          <Button
            variant="secondary"
            disabled={page >= events.data.page_count}
            onClick={() => setPage((current) => current + 1)}
          >
            Next
          </Button>
        </nav>
      )}
    </>
  )

  if (embedded) {
    return (
      <section className="tc-audit-embedded">
        <header className="tc-audit-section-head">
          <div>
            <h2>Audit log</h2>
            <p>See who signed in and which administrative changes were made.</p>
          </div>
          <Button variant="primary" icon={<Icon.download size={16} />} disabled={exportLog.isPending} onClick={() => exportLog.mutate()}>
            Export CSV
          </Button>
        </header>
        {content}
      </section>
    )
  }

  return (
    <div className="tc-page tc-audit-page">
      <PageHeader
        eyebrow={isSiteAdmin ? 'Platform administration' : 'School administration'}
        title="Audit log"
        icon={<Icon.audit size={20} />}
        description="See who signed in and which administrative changes were made."
        actions={
          <Button variant="primary" icon={<Icon.download size={16} />} disabled={exportLog.isPending} onClick={() => exportLog.mutate()}>
            Export CSV
          </Button>
        }
      />
      {content}
    </div>
  )
}

function AuditTable({ events, showSchool }: { events: AuditEvent[]; showSchool: boolean }) {
  return (
    <div className="tc-table-wrap tc-audit-table-wrap">
      <table className="tc-audit-table">
        <thead>
          <tr>
            <th>Time</th>
            <th>Person</th>
            {showSchool && <th>School</th>}
            <th>Activity</th>
            <th>Target</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {events.map((event) => (
            <tr key={event.id}>
              <td data-label="Time"><time dateTime={event.created_at}>{when(event.created_at)}</time></td>
              <td data-label="Person">
                <strong>{event.actor_name}</strong>
                <span className="tc-audit-secondary">{event.actor_email}</span>
              </td>
              {showSchool && <td data-label="School">{event.school_name ?? 'Platform'}</td>}
              <td data-label="Activity"><strong>{event.action_label}</strong></td>
              <td data-label="Target">{targetLabel(event)}</td>
              {/* There is deliberately no "Outcome" column. It rendered a green
                  "Recorded" pill on every row, because the log only ever holds
                  events that happened - so it was a seventh column, on the
                  widest table in the app, that could not tell you anything. */}
              <td data-label="Source">
                <span>{event.ip_address ?? 'Not available'}</span>
                <span className="tc-audit-secondary">{deviceLabel(event.user_agent)}</span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function toApiFilters(filters: FilterState, page: number, fixedSchoolId?: string): AuditFilters {
  return {
    q: filters.q || undefined,
    school_id: (fixedSchoolId ?? filters.schoolId) || undefined,
    action: filters.action || undefined,
    from_at: filters.from ? new Date(`${filters.from}T00:00:00`).toISOString() : undefined,
    to_at: filters.to ? new Date(`${filters.to}T23:59:59.999`).toISOString() : undefined,
    page,
    per_page: 50,
  }
}

function when(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value))
}

function targetLabel(event: AuditEvent) {
  if (!event.target_type) return '—'
  const type = event.target_type.replaceAll('_', ' ')
  return event.target_id ? `${type} · ${event.target_id.slice(0, 8)}` : type
}

function deviceLabel(userAgent: string | null) {
  if (!userAgent) return 'Device not available'
  if (/Edg\//.test(userAgent)) return 'Microsoft Edge'
  if (/Chrome\//.test(userAgent)) return 'Google Chrome'
  if (/Firefox\//.test(userAgent)) return 'Mozilla Firefox'
  if (/Safari\//.test(userAgent)) return 'Safari'
  return 'Browser session'
}
